import type { ComponentType } from 'react';

import { type DocumentNode, type ErrorLike } from '@apollo/client';

import type {
  GetAgencyEditDetailsQuery,
  GetTeamMemberEditDetailsQuery,
} from '@graphql/__generated__/graphql';
import type { GenericEditModalType } from '@modules/_shared/components/visibility-drawer/edit-modals/editModalConfig';

export type EditModalType = GenericEditModalType | 'agentPhone';

export type AgentEntity = NonNullable<
  GetTeamMemberEditDetailsQuery['users_by_pk']
>;

export type AgencyEntity = NonNullable<
  GetAgencyEditDetailsQuery['teams_by_pk']
>;

export type VisibilityEntity = AgentEntity | AgencyEntity;

export const isAgentEntity = (
  entity: VisibilityEntity,
): entity is AgentEntity => {
  return '__typename' in entity && entity.__typename === 'users';
};

export const isAgencyEntity = (
  entity: VisibilityEntity,
): entity is AgencyEntity => {
  return '__typename' in entity && entity.__typename === 'teams';
};

export const resolveTeamPhone = (
  entity: AgentEntity,
  contextId: string | undefined,
) => {
  const phones =
    entity.teams_users?.find(tu => tu.team.id === contextId)?.team
      ?.phone_numbers ?? [];
  return phones.find(pn => pn.type === 'proxy') ?? phones[0] ?? null;
};

export type DrawerSection<T = SectionProps> = {
  id:
    | Omit<EditModalType, 'reviews' | 'transactions'>
    | 'reviews'
    | 'transactions'
    | 'onlineListings';
  title?: string;
  component: ComponentType<T>;
  customProps?: Record<string, unknown>;
};

export type DrawerSectionGroup = {
  id: string;
  sections: DrawerSection[];
};

export type SectionProps<TEntity extends VisibilityEntity = VisibilityEntity> =
  {
    entity: TEntity;
    onEdit?: () => void;
    teamId?: string;
  };

export type DrawerConfig<TData, TEntity> = {
  entityType: 'user' | 'team';
  query: DocumentNode;
  extractEntity: (data: TData) => TEntity | null;
  sectionGroups: DrawerSectionGroup[];
};

export type EditModalRegistry = {
  [modalType: string]: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    entity: VisibilityEntity;
    onSave?: () => void;
  }>;
};

export type DrawerContextData = {
  entity: VisibilityEntity | null;
  setOpenModal: (modalType: EditModalType) => void;
  contextId?: string;
};

export const getSlugConstraintError = (
  error: ErrorLike,
  t: (key: string) => string,
): string | null => {
  if (error.message.includes('unique_agent_slug_index')) {
    return t('This slug is already used by another agent');
  }
  if (error.message.includes('users_show_in_agency_pages_agent_slug_check')) {
    return t('The profile must have a slug while it is published');
  }
  return null;
};
